import { useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { generateContentHash, checkImageDuplicate, checkFilePathConflict } from '@/utils/imageHash'
import { resizeImage, formatFileSize } from '@/utils/imageResizer'
import { resolveStorageUrl } from '@/utils/storageUrl'

export interface EventImage {
  id: string
  event_id: string
  event_type: string
  file_path: string
  file_name: string
  file_size: number
  mime_type: string
  is_primary: boolean
  is_active: boolean
  created_at: string
  category?: string
  display_order?: number
  caption?: string
  alt_text?: string
  publicUrl?: string
  content_hash?: string
}

export interface DuplicateCheckResult {
  isDuplicate: boolean
  existingImage?: EventImage
  category?: string
}

import { EventType } from '@/utils/eventUtils';

interface UseImageUploadProps {
  eventId: string
  eventType: EventType
  maxFileSize?: number // in MB
  maxFiles?: number // maximum number of files
  enableResize?: boolean // Enable automatic image resizing (default: true)
}

export const useImageUpload = ({ 
  eventId, 
  eventType, 
  maxFileSize = 10, // Increased default since we resize
  maxFiles = 40,
  enableResize = true
}: UseImageUploadProps) => {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [resizingStatus, setResizingStatus] = useState<string>('')

  const checkForDuplicates = async (file: File, targetCategory: string): Promise<DuplicateCheckResult> => {
    try {
      // Generate content hash
      const contentHash = await generateContentHash(file)
      
      // Check for content duplicates
      const duplicateCheck = await checkImageDuplicate(supabase, eventId, eventType, contentHash)
      
      return duplicateCheck
    } catch (error) {
      console.warn('Error checking duplicates:', error)
      return { isDuplicate: false }
    }
  }

  const uploadImage = async (file: File, category: string = 'general', ignoreDuplicates: boolean = false): Promise<EventImage> => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image')
    }

    // Determine target size based on category
    // Hero/thumbnail images: 500KB, all others: 250KB
    const targetSizeKB = (category === 'hero' || category === 'thumbnail') ? 500 : 250;

    // Resize image if enabled and file is large
    let fileToUpload = file;
    if (enableResize && file.size > targetSizeKB * 1024) {
      try {
        setResizingStatus('Processing image...');
        const resizeResult = await resizeImage(file, {
          targetSizeKB,
          maxSizeKB: 1000,
          maxDimension: 1920,
          onProgress: (status) => setResizingStatus(status)
        });
        
        fileToUpload = resizeResult.file;
        
        if (resizeResult.wasResized) {
          console.log(`📏 Image resized: ${formatFileSize(resizeResult.originalSize)} → ${formatFileSize(resizeResult.finalSize)}`);
        }
        
        setResizingStatus('');
      } catch (error) {
        console.warn('Resize failed, uploading original:', error);
        setResizingStatus('');
        // Continue with original file if resize fails
      }
    }

    // Validate final file size
    if (fileToUpload.size > maxFileSize * 1024 * 1024) {
      throw new Error(`File is too large. Maximum size is ${maxFileSize}MB`)
    }

    // Ensure user is authenticated for RLS policy (attempt refresh if needed)
    let { data: { session } } = await supabase.auth.getSession()
    if (!session?.user) {
      try {
        await supabase.auth.refreshSession()
      } catch (e) {
        // ignore, will re-check session below
      }
      ;({ data: { session } } = await supabase.auth.getSession())
    }
    const user = session?.user
    if (!user) {
      toast.error('Authentication required. Please sign in and try again.')
      throw new Error('Authentication required')
    }

    // Check for duplicates unless ignored
    if (!ignoreDuplicates) {
      const duplicateCheck = await checkForDuplicates(file, category)
      if (duplicateCheck.isDuplicate) {
        const existingCategory = duplicateCheck.category || 'unknown'
        throw new Error(`This image already exists in the "${existingCategory}" category. Use "Move" to relocate it instead.`)
      }
    }

    // Generate content hash
    const contentHash = await generateContentHash(file)

    // Generate unique file name
    const fileExtension = fileToUpload.name.split('.').pop() || file.name.split('.').pop()
    const fileName = `${eventType}-${eventId}-${Date.now()}.${fileExtension}`
    const filePath = `${eventType}/${eventId}/${fileName}`
    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from('event-images')
      .upload(filePath, fileToUpload, {
        cacheControl: '3600',
        upsert: false
      })

    if (uploadError) throw uploadError

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('event-images')
      .getPublicUrl(filePath)

    // Save to database
    const { data: imageData, error: dbError } = await supabase
      .from('event_images')
      .insert({
        event_id: eventId,
        event_type: eventType,
        file_path: filePath,
        file_name: file.name,
        file_size: fileToUpload.size, // Use resized file size
        mime_type: fileToUpload.type, // Use resized file type
        category: category,
        content_hash: contentHash,
        is_primary: false,
        uploaded_by: user?.id || null
      })
      .select()
      .single()

    if (dbError) throw dbError

    return { ...imageData, publicUrl }
  }

  const uploadImages = async (files: File[], category: string = 'general', ignoreDuplicates: boolean = false): Promise<EventImage[]> => {
    setUploading(true)
    setProgress(0)

    try {
      const results: EventImage[] = []
      const duplicates: string[] = []

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        try {
          const result = await uploadImage(file, category, ignoreDuplicates)
          results.push(result)
        } catch (error) {
          if (error instanceof Error && error.message.includes('already exists')) {
            duplicates.push(file.name)
          } else {
            throw error
          }
        }
        setProgress(((i + 1) / files.length) * 100)
      }

      if (results.length > 0) {
        toast.success(`Successfully uploaded ${results.length} image(s)`)
      }
      
      if (duplicates.length > 0) {
        toast.warning(`${duplicates.length} duplicate(s) skipped: ${duplicates.join(', ')}`)
      }

      return results
    } catch (error) {
      console.error('Upload error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to upload images')
      throw error
    } finally {
      setUploading(false)
      setProgress(0)
    }
  }

  const removeFromGallery = async (imageId: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('event_images')
        .update({ 
          category: 'general',
          is_primary: false
        })
        .eq('id', imageId)

      if (error) throw error

      toast.success('Image removed from gallery')
    } catch (error) {
      console.error('Remove from gallery error:', error)
      toast.error('Failed to remove image from gallery')
      throw error
    }
  }

  const moveToCategory = async (imageId: string, newCategory: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('event_images')
        .update({ 
          category: newCategory,
          is_primary: false
        })
        .eq('id', imageId)

      if (error) throw error

      toast.success(`Image moved to ${newCategory}`)
    } catch (error) {
      console.error('Move to category error:', error)
      toast.error('Failed to move image')
      throw error
    }
  }

  const toggleActiveStatus = async (imageId: string, isActive: boolean): Promise<void> => {
    try {
      const { error } = await supabase
        .from('event_images')
        .update({ 
          is_active: isActive,
          is_primary: isActive ? undefined : false
        })
        .eq('id', imageId)

      if (error) throw error

      toast.success(`Image ${isActive ? 'activated' : 'deactivated'}`)
    } catch (error) {
      console.error('Toggle active status error:', error)
      toast.error('Failed to update image status')
      throw error
    }
  }

  const permanentlyDeleteImage = async (imageId: string, filePath: string): Promise<void> => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('event-images')
        .remove([filePath])

      if (storageError) throw storageError

      // Delete from database
      const { error: dbError } = await supabase
        .from('event_images')
        .delete()
        .eq('id', imageId)

      if (dbError) throw dbError

      toast.success('Image permanently deleted')
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete image')
      throw error
    }
  }

  const setPrimaryImage = async (imageId: string): Promise<void> => {
    try {
      // First, set all images to not primary
      await supabase
        .from('event_images')
        .update({ is_primary: false })
        .eq('event_id', eventId)

      // Then set the selected image as primary
      const { error } = await supabase
        .from('event_images')
        .update({ is_primary: true })
        .eq('id', imageId)

      if (error) throw error

      toast.success('Primary image updated')
    } catch (error) {
      console.error('Primary image error:', error)
      toast.error('Failed to set primary image')
      throw error
    }
  }

  const fetchEventImages = async (includeInactive: boolean = false): Promise<EventImage[]> => {
    try {
      let query = supabase
        .from('event_images')
        .select('id, event_id, event_type, file_path, file_name, file_size, mime_type, alt_text, caption, category, display_order, is_active, is_primary, created_at')
        .eq('event_id', eventId)

      if (!includeInactive) {
        query = query.eq('is_active', true)
      }

      const { data, error } = await query.order('created_at', { ascending: false })

      if (error) throw error

      return (data || []).map(image => ({
        ...image,
        publicUrl: resolveStorageUrl(image.file_path) || undefined
      }))
    } catch (error) {
      // Only log errors, not info
      return []
    }
  }

  const updateImageMeta = async (imageId: string, updates: Partial<EventImage>) => {
    try {
      const { error } = await supabase
        .from('event_images')
        .update(updates)
        .eq('id', imageId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating image metadata:', error);
      throw error;
    }
  };

  const updateDisplayOrder = async (imageIdsInOrder: string[]) => {
    try {
      for (let i = 0; i < imageIdsInOrder.length; i++) {
        await supabase
          .from('event_images')
          .update({ display_order: i })
          .eq('id', imageIdsInOrder[i]);
      }
    } catch (error) {
      console.error('Error updating display order:', error);
      throw error;
    }
  };

  return {
    uploading,
    progress,
    resizingStatus,
    uploadImage,
    uploadImages,
    removeFromGallery,
    moveToCategory,
    toggleActiveStatus,
    permanentlyDeleteImage,
    setPrimaryImage,
    fetchEventImages,
    checkForDuplicates,
    updateImageMeta,
    updateDisplayOrder
  }
}